import { CarTwoTone } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import GoogleMapReact from 'google-map-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { QueryHookRuntimeOptions } from '@/common/hooks/typedHookFactory';
import { useDepartments } from '@/common/useDepartments';
import { getStatusColor } from '@/core/utils/get_status_color';
import type {
  GetDriversWithAddressByDateQuery,
  GetDriversWithLastTrackPointQuery,
} from '@/generated/graphql/graphql';
import { mapSettings } from '@/utils/mapSettings';
import { MIN_ZOOM_FOR_NAVIGATION, POLLING_INTERVAL_MS, SELECTED_DRIVER_ZOOM } from '../constants';
import { useEventMarkersClustering } from '../hooks/useEventMarkersClustering';
import { usePolylineLifecycle } from '../hooks/usePolylineLifecycle';
import { useTrackingPolygons } from '../hooks/useTrackingPolygons';
import { useGetDriversWithAddressByDate, useGetDriverTrackPoints } from '../query/tracking.hooks';
import { getDayRange } from '../utils/dateRange';
import {
  countExpressOrders,
  getExpressDriverColor,
  hasAnyExpressOrder,
} from '../utils/expressOrdersUtils';
import { clearMapOverlays, drawPolygonsOnMap, type GeoEventMarkerData } from '../utils/mapOverlays';
import { getOrderChangeStatusName, type OrderChange } from '../utils/orderChange';
import { GeoEventHighlightMarker } from './GeoEventHighlightMarker';
import { Marker } from './Marker';

type Driver = GetDriversWithLastTrackPointQuery['user'][0];
type DriverWithAddressOrder = GetDriversWithAddressByDateQuery['user'][0]['orders'][0];

interface SelectedOrder {
  id: number;
  lat: number;
  lng: number;
}

interface GeoEventHighlight {
  lat: number;
  lng: number;
  statusName: string;
}

const PRIMARY_COLOR = '#1890ff';
const PRIMARY_SHADOW = '0 4px 8px rgba(24, 144, 255, 0.4)';

const DRIVER_MARKER_BASE_SIZE = 24;
const DRIVER_MARKER_SELECTED_SIZE = 32;
const DRIVER_MARKER_EXPRESS_SIZE = 28;

const ORDER_MARKER_BASE_SIZE = 24;
const ORDER_MARKER_SELECTED_SIZE = 32;

const CRITICAL_EXPRESS_COLOR = '#8B0000';
const WARNING_EXPRESS_COLOR = '#FF0000';

const SMOOTH_PAN_ZOOM_DELAY_MS = 200;
const CENTER_ON_SELECTION_DELAY_MS = 100;
const DEFAULT_MAP_CENTER = { lat: -12.04318, lng: -77.02824 };
const DEFAULT_MAP_ZOOM = 12;

function getDriverExpressInfo(
  driver: Driver,
  departmentTimeZone: number
): { expressColor: string | null; hasExpress: boolean } {
  if (!driver.orders) {
    return { expressColor: null, hasExpress: false };
  }
  return {
    expressColor: getExpressDriverColor(driver.orders, departmentTimeZone),
    hasExpress: countExpressOrders(driver.orders, departmentTimeZone) > 0,
  };
}

function getDriverMarkerSize(isSelected: boolean, hasExpress: boolean): number {
  if (isSelected) return DRIVER_MARKER_SELECTED_SIZE;
  if (hasExpress) return DRIVER_MARKER_EXPRESS_SIZE;
  return DRIVER_MARKER_BASE_SIZE;
}

function getDriverMarkerAnimation(
  hasExpress: boolean,
  expressColor: string | null
): string | undefined {
  if (!hasExpress) return undefined;
  if (expressColor === CRITICAL_EXPRESS_COLOR) return 'pulse-critical 2s infinite';
  if (expressColor === WARNING_EXPRESS_COLOR) return 'pulse-warning 3s infinite';
  return undefined;
}

function getDriverMarkerIconStyle(
  hasExpress: boolean,
  expressColor: string | null,
  isSelected: boolean,
  animation: string | undefined
): React.CSSProperties {
  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    border: !isSelected && hasExpress ? `3px solid ${expressColor || '#FFD700'}` : 'none',
    borderRadius: '50%',
    padding: hasExpress && !isSelected ? '2px' : '0px',
    animation,
  };
}

function getDriverCarIconStyle(isSelected: boolean, hasExpress: boolean): React.CSSProperties {
  return {
    opacity: isSelected ? 1 : 0.7,
    fontSize: isSelected ? '24px' : hasExpress ? '20px' : '16px',
  };
}

function getOrderMarkerSize(isSelected: boolean): number {
  return isSelected ? ORDER_MARKER_SELECTED_SIZE : ORDER_MARKER_BASE_SIZE;
}

function getOrderMarkerContainerStyle(
  isSelected: boolean,
  statusName: string | null | undefined,
  isSelectedDriverOrder: boolean
): React.CSSProperties {
  return {
    background: getStatusColor(statusName ?? ''),
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: isSelected ? '14px' : '12px',
    fontWeight: 'bold',
    border: isSelected ? `3px solid ${PRIMARY_COLOR}` : '2px solid #fff',
    boxShadow: isSelected ? PRIMARY_SHADOW : '0 2px 4px rgba(0,0,0,0.3)',
    transform: isSelected ? 'scale(1.1)' : 'scale(1)',
    transition: 'all 0.2s ease',
    zIndex: isSelected ? 1000 : isSelectedDriverOrder ? 100 : 10,
  };
}

interface TrackingMapViewProps {
  selectedDriverId: string | undefined;
  selectedDate: Dayjs;
  drivers: GetDriversWithLastTrackPointQuery | undefined;
  showExpressOnly: boolean;
  selectedOrder: SelectedOrder | null;
  geoEventHighlight: GeoEventHighlight | null;
  orderChanges: ReadonlyArray<OrderChange> | undefined;
  isToday: boolean;
  onDriverSelect?: (driverId: string) => void;
  onOrderSelect?: (order: SelectedOrder) => void;
}

export const TrackingMapView: React.FC<TrackingMapViewProps> = ({
  selectedDriverId,
  selectedDate,
  drivers,
  showExpressOnly,
  selectedOrder,
  geoEventHighlight,
  orderChanges,
  isToday,
  onDriverSelect,
  onOrderSelect,
}) => {
  const { selectedDepartmentId: departmentId, selectedDepartment } = useDepartments();
  const departmentTimeZone = selectedDepartment?.timeZone ?? 0;
  const { dateFrom, dateTo } = useMemo(() => getDayRange(selectedDate), [selectedDate]);
  const runtimeOptions = useMemo<QueryHookRuntimeOptions>(
    () => ({ refetchInterval: isToday ? POLLING_INTERVAL_MS : (false as const) }),
    [isToday]
  );

  const { data } = useGetDriversWithAddressByDate(
    {
      id: selectedDriverId ?? '',
      departmentId,
      dateFrom,
      dateTo,
    },
    runtimeOptions
  );

  const trackingPolygons = useTrackingPolygons(departmentId, selectedDriverId, selectedDate);

  const { data: trackPointsData } = useGetDriverTrackPoints(
    {
      driverId: selectedDriverId ?? '',
      dateFrom,
      dateTo,
    },
    runtimeOptions
  );

  const [center, setCenter] = useState<{ lat: number; lng: number }>(DEFAULT_MAP_CENTER);
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const orders = data?.user[0]?.orders ?? [];

  const updateCenterFromFirstOrder = useCallback(() => {
    if (!data?.user[0]?.orders.length) return;

    const firstOrder = data.user[0].orders[0];
    if (firstOrder?.client?.address?.lat && firstOrder.client.address.lng) {
      setCenter({
        lat: firstOrder.client.address.lat,
        lng: firstOrder.client.address.lng,
      });
    }
  }, [data]);

  const fitMapToDriversBounds = useCallback(() => {
    if (!mapRef.current || !drivers?.user || !window.google?.maps) return;

    const bounds = new window.google.maps.LatLngBounds();
    drivers.user.forEach((driver) => {
      if (driver.track_points[0]) {
        bounds.extend({
          lat: driver.track_points[0].lat,
          lng: driver.track_points[0].lng,
        });
      }
    });

    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds);
    }
  }, [drivers]);

  const smoothPanTo = useCallback((lat: number, lng: number, targetZoom?: number) => {
    if (!mapRef.current) return;

    const currentZoom = mapRef.current.getZoom() ?? DEFAULT_MAP_ZOOM;

    mapRef.current.panTo({ lat, lng });

    if (targetZoom !== undefined && Math.abs(currentZoom - targetZoom) > 1) {
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.setZoom(targetZoom);
        }
      }, SMOOTH_PAN_ZOOM_DELAY_MS);
    }
  }, []);

  const centerMapOnSelectedOrder = useCallback(() => {
    if (!mapRef.current || !selectedOrder) return;

    setTimeout(() => {
      if (mapRef.current && selectedOrder) {
        smoothPanTo(selectedOrder.lat, selectedOrder.lng, MIN_ZOOM_FOR_NAVIGATION);
      }
    }, CENTER_ON_SELECTION_DELAY_MS);
  }, [selectedOrder, smoothPanTo]);

  const centerMapOnSelectedDriver = useCallback(() => {
    if (!mapRef.current || !selectedDriverId || !drivers?.user) return;
    if (selectedOrder) return;

    const selectedDriver = drivers.user.find((driver) => driver.id === selectedDriverId);
    if (selectedDriver?.track_points[0]) {
      setTimeout(() => {
        if (mapRef.current && selectedDriver?.track_points[0]) {
          smoothPanTo(
            selectedDriver.track_points[0].lat,
            selectedDriver.track_points[0].lng,
            SELECTED_DRIVER_ZOOM
          );
        }
      }, CENTER_ON_SELECTION_DELAY_MS);
    }
  }, [selectedDriverId, drivers, selectedOrder, smoothPanTo]);

  useEffect(() => {
    if (!selectedOrder && !selectedDriverId) {
      updateCenterFromFirstOrder();
    }
  }, [updateCenterFromFirstOrder, selectedOrder, selectedDriverId]);

  useEffect(() => {
    if (!selectedOrder && !selectedDriverId) {
      fitMapToDriversBounds();
    }
  }, [fitMapToDriversBounds, selectedOrder, selectedDriverId]);

  useEffect(() => {
    if (selectedOrder) {
      centerMapOnSelectedOrder();
    } else if (selectedDriverId) {
      centerMapOnSelectedDriver();
    }
  }, [selectedOrder, selectedDriverId, centerMapOnSelectedOrder, centerMapOnSelectedDriver]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;

    const overlays = drawPolygonsOnMap(map, trackingPolygons);
    return () => clearMapOverlays(overlays);
  }, [trackingPolygons, mapLoaded]);

  usePolylineLifecycle({
    map: mapRef.current,
    mapLoaded,
    enabled: Boolean(selectedDriverId),
    points: trackPointsData?.track_point,
  });

  const eventMarkersData = useMemo<GeoEventMarkerData[]>(() => {
    if (!selectedDriverId || selectedOrder || !orderChanges) return [];

    return orderChanges.flatMap((change) => {
      const geoEvent = change.geo_event;
      if (!geoEvent) return [];
      const orderIndex = orders.findIndex((o) => o.id === change.orderId);
      if (orderIndex === -1) return [];
      const statusName = getOrderChangeStatusName(change);
      return [
        {
          lat: geoEvent.lat,
          lng: geoEvent.lng,
          statusName,
          label: String(orderIndex + 1),
          title: `Order ${orders[orderIndex]?.shortCode ?? change.orderId}: ${statusName}`,
        },
      ];
    });
  }, [selectedDriverId, selectedOrder, orderChanges, orders]);

  useEventMarkersClustering({
    map: mapRef.current,
    mapLoaded,
    enabled: Boolean(selectedDriverId) && !selectedOrder,
    events: eventMarkersData,
  });

  const renderDriverMarker = (driver: Driver) => {
    if (!driver.track_points[0]) return null;

    const { expressColor, hasExpress } = getDriverExpressInfo(driver, departmentTimeZone);
    const isSelected = selectedDriverId === driver.id;
    const markerSize = getDriverMarkerSize(isSelected, hasExpress);
    const animation = getDriverMarkerAnimation(hasExpress, expressColor);
    const iconStyle = getDriverMarkerIconStyle(hasExpress, expressColor, isSelected, animation);
    const carIconStyle = getDriverCarIconStyle(isSelected, hasExpress);

    return (
      <Marker
        key={driver.id}
        lat={driver.track_points[0].lat}
        lng={driver.track_points[0].lng}
        size={markerSize}
        onClick={onDriverSelect ? () => onDriverSelect(driver.id) : undefined}
        containerStyle={{
          border: isSelected ? `3px solid ${PRIMARY_COLOR}` : undefined,
          boxShadow: isSelected ? PRIMARY_SHADOW : '0 0 5px #0004',
          transform: isSelected ? 'scale(1.1)' : 'scale(1)',
          transition: 'all 0.2s ease',
          zIndex: isSelected ? 1000 : 1,
        }}
        icon={
          <div style={iconStyle}>
            <CarTwoTone style={carIconStyle} />
          </div>
        }
      />
    );
  };

  const renderOrderMarker = (order: DriverWithAddressOrder, index: number) => {
    const lat = order?.client?.address?.lat;
    const lng = order?.client?.address?.lng;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const isSelected = selectedOrder?.id === order.id;
    const isSelectedDriverOrder = data?.user[0]?.id === selectedDriverId;
    const markerSize = getOrderMarkerSize(isSelected);
    const containerStyle = getOrderMarkerContainerStyle(
      isSelected,
      order.statusName,
      isSelectedDriverOrder
    );

    const handleOrderClick = () => {
      if (onOrderSelect) {
        onOrderSelect({
          id: order.id,
          lat: lat as number,
          lng: lng as number,
        });
      }
    };

    return (
      <Marker
        key={order.id}
        lat={lat as number}
        lng={lng as number}
        size={markerSize}
        onClick={handleOrderClick}
        containerStyle={containerStyle}
        icon={<span>{index + 1}</span>}
      />
    );
  };

  const filteredDrivers =
    drivers?.user?.filter((driver) => {
      if (!showExpressOnly) return true;
      return driver.orders && hasAnyExpressOrder(driver.orders);
    }) ?? [];

  const selectedDriver = filteredDrivers.find((driver) => driver.id === selectedDriverId);
  const otherDrivers = filteredDrivers.filter((driver) => driver.id !== selectedDriverId);

  const selectedOrderData = selectedOrder
    ? orders.find((order) => order.id === selectedOrder.id)
    : null;
  const otherOrders = orders.filter((order) => order.id !== selectedOrder?.id);

  return (
    <GoogleMapReact
      bootstrapURLKeys={{ key: '' }}
      center={center}
      defaultZoom={DEFAULT_MAP_ZOOM}
      options={mapSettings}
      yesIWantToUseGoogleMapApiInternals
      onGoogleApiLoaded={({ map }) => {
        mapRef.current = map;
        setMapLoaded(true);
      }}
    >
      {otherDrivers.map((driver) => renderDriverMarker(driver))}

      {selectedDriver &&
        otherOrders.map((order) => {
          const originalIndex = orders.findIndex((o) => o.id === order.id);
          return renderOrderMarker(order, originalIndex);
        })}

      {selectedDriver && renderDriverMarker(selectedDriver)}

      {selectedOrderData &&
        (() => {
          const originalIndex = orders.findIndex((o) => o.id === selectedOrderData.id);
          return renderOrderMarker(selectedOrderData, originalIndex);
        })()}

      {geoEventHighlight && (
        <GeoEventHighlightMarker
          lat={geoEventHighlight.lat}
          lng={geoEventHighlight.lng}
          statusName={geoEventHighlight.statusName}
        />
      )}
    </GoogleMapReact>
  );
};
